import { v4 as uuid } from 'uuid';

import {
  IFilterConfigChildren,
  IFilterConfigItem,
  IFilterConfigItemGroup
} from '@/bundles/GroupFilters/interfaces/IFilterConfigItem';
import { getPropertyFilters } from '@/bundles/GroupFilters/helpers/filtersConfig/property';
import { Nullable } from '@/utils/types';
import { IOldFiltersFromSet } from '@/bundles/GroupFilters/interfaces/mappers/IOldFiltersFromSet';
import { IGroupFilters } from '@/bundles/GroupFilters/helpers/getDefaultFiltersObj';
import { getFormattedValueByConfig } from '@/bundles/GroupFilters/helpers/formatters/getFormattedValueByConfig';
import {
  FILTER_KEYS_WITHOUT_CONFIG,
  mapOldFilterWithoutConfig
} from '@/bundles/GroupFilters/helpers/mappers/mapOldFilterWithoutConfig';
import { mapFiltersWithAdditionalInfoFromFilterSets } from '@/bundles/GroupFilters/helpers/mappers/mapFiltersWithAdditionalInfoFromFilterSets';
import { getAvailabilityFilters } from '@/bundles/GroupFilters/helpers/filtersConfig/availability';
import { getComparableFilters } from '@/bundles/GroupFilters/helpers/filtersConfig/comparable';
import { isValuePresentInFilter } from '@/bundles/GroupFilters/helpers/mappers/isValuePresentInFilter';
import { getDealFilters } from '@/bundles/GroupFilters/helpers/filtersConfig/deal';
import { getCompanyFilters } from '@/bundles/GroupFilters/helpers/filtersConfig/company';
import { getContactFilters } from '@/bundles/GroupFilters/helpers/filtersConfig/contact';
import { getOccupierFilters } from '@/bundles/GroupFilters/helpers/filtersConfig/occupiers';
import { getRequirementFilters } from '@/bundles/GroupFilters/helpers/filtersConfig/requirement';
import { getProjectFilters } from '@/bundles/GroupFilters/helpers/filtersConfig/project';
import { getSavedSetFilters } from '@/bundles/GroupFilters/helpers/filtersConfig/savedSet';
import { getPipelineFilters } from '@/bundles/GroupFilters/helpers/filtersConfig/pipeline';
import { getPressReleaseFilters } from '@/bundles/GroupFilters/helpers/filtersConfig/pressRelease';
import { getMemberFilters } from '@/bundles/GroupFilters/helpers/filtersConfig/member';
import { getRequestFilters } from '@/bundles/GroupFilters/helpers/filtersConfig/request';
import { getSignListFilters } from '@/bundles/GroupFilters/helpers/filtersConfig/signList';
import { getMarketingCampaignFilters } from '@/bundles/GroupFilters/helpers/filtersConfig/marketingCampaign';
import { GROUP_WITH_STANDALONE_FILTERS } from '@/bundles/GroupFilters/consts';
import { getEblastFilters } from '@/bundles/GroupFilters/helpers/filtersConfig/eblast';

const configDictionary = {
  property: getPropertyFilters,
  availability: getAvailabilityFilters,
  comparable: getComparableFilters,
  deal: getDealFilters,
  company: getCompanyFilters,
  contact: getContactFilters,
  occupier: getOccupierFilters,
  requirement: getRequirementFilters,
  project: getProjectFilters,
  savedSet: getSavedSetFilters,
  pipeline: getPipelineFilters,
  pressrelease: getPressReleaseFilters,
  member: getMemberFilters,
  request: getRequestFilters,
  signList: getSignListFilters,
  marketingCampaign: getMarketingCampaignFilters,
  eblast: getEblastFilters,
};

const getConfigForFilterName = (name: string, source: string): Nullable<IFilterConfigItem | IFilterConfigChildren> => {
  if (!(source in configDictionary)) {
    return null;
  }

  const foundConfig = configDictionary[source]().find((config: IFilterConfigItem | IFilterConfigItemGroup) => {
    if ('children' in config) {
      return config.children.some(child => child.name === name);
    }

    return config.name === name;
  }) || null;

  if (!foundConfig) {
    return null;
  }

  if ('children' in foundConfig) {
    return foundConfig.children.find((child: IFilterConfigChildren) => child.name === name) || null;
  }

  return foundConfig;
}

export const mapOldFiltersFromSetToNew = (filterGroups: Array<IOldFiltersFromSet[]>, source: string, currentUserId?: number): IGroupFilters => {
  const mappedFilters: IGroupFilters = {};

  filterGroups.forEach((filterGroup) => {
    // filter empty values from filter sets
    filterGroup = filterGroup.filter(filter => isValuePresentInFilter(filter.value));

    const isGroupOutsideFiltersPanel = filterGroup.some(filter => FILTER_KEYS_WITHOUT_CONFIG.includes(filter.name));
    const groupId = isGroupOutsideFiltersPanel ? GROUP_WITH_STANDALONE_FILTERS : uuid();

    filterGroup.forEach((filter) => {
      if (!mappedFilters[groupId]) {
        mappedFilters[groupId] = {};
      }

      const config = getConfigForFilterName(filter.name, source);

      if (!config) {
        const mappedFilterWithoutConfig = mapOldFilterWithoutConfig(filter, currentUserId);

        if (mappedFilterWithoutConfig) {
          mappedFilters[groupId] = { ...mappedFilters[groupId], ...mappedFilterWithoutConfig };
        }

        return;
      }

      mappedFilters[groupId][filter.name] = {
        id: uuid(),
        title: config.title,
        value: filter.value,
        formattedValue: getFormattedValueByConfig(filter.value, config),
      };

      mappedFilters[groupId] = mapFiltersWithAdditionalInfoFromFilterSets({
        oldFiltersGroup: filterGroup,
        mappedFiltersGroup: mappedFilters[groupId]
      });
    });
  });

  return mappedFilters;
}
